import { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp, CreditCard, Filter } from "lucide-react";

interface PaymentsTableProps {
  customers: Customer[];
  products: Product[];
  jobs: string[];
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
  onSelectCustomer?: (customer: Customer) => void;
}

export function PaymentsTable({ customers, products, jobs, onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate, onSelectCustomer }: PaymentsTableProps) {
  // خوێندنەوەی ڕیزکردن لە localStorage
  const [sortOption, setSortOption] = useState<string>(() => {
    return localStorage.getItem('payments-sort-option') || 'none';
  });
  
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // تۆمارکردنی ڕیزکردن لە localStorage کاتێک دەگۆڕێت
  useEffect(() => {
    localStorage.setItem('payments-sort-option', sortOption);
  }, [sortOption]);

  const sortOptions = [
    { value: 'none', label: 'بێ ڕیزکردن', icon: Filter },
    { value: 'code-asc', label: 'کۆد: لە بچووک بۆ گەورە', icon: ArrowUp },
    { value: 'code-desc', label: 'کۆد: لە گەورە بۆ بچووک', icon: ArrowDown },
    { value: 'name-asc', label: 'ناو: ئەلف - یا', icon: ArrowUp },
    { value: 'name-desc', label: 'ناو: یا - ئەلف', icon: ArrowDown },
    { value: 'remaining-desc', label: 'ماوە: لە زۆر بۆ کەم', icon: ArrowDown },
    { value: 'remaining-asc', label: 'ماوە: لە کەم بۆ زۆر', icon: ArrowUp },
  ];

  const toggleExpanded = (customerId: string) => {
    setExpandedCustomers(prev => {
      const newSet = new Set<string>();
      if (!prev.has(customerId)) {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    if (onSelectCustomer) {
      onSelectCustomer(customer);
    }
  };

  // Calculate totals for each customer
  const customersWithTotals = useMemo(() => {
    return customers.map(customer => {
      const customerProducts = products.filter(p => p.customerId === customer.id);
      
      const totalProducts = customerProducts.length;
      
      // IQD totals
      const iqdProducts = customerProducts.filter(p => p.currency === "IQD");
      const totalProductsIQD = iqdProducts.length;
      const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
      const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
      const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
      
      // USD totals
      const usdProducts = customerProducts.filter(p => p.currency === "USD");
      const totalProductsUSD = usdProducts.length;
      const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
      const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
      const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

      return {
        ...customer,
        totalProducts,
        totalProductsIQD,
        totalProductsUSD,
        totalSaleIQD,
        totalPaidIQD,
        totalRemainingIQD,
        totalSaleUSD,
        totalPaidUSD,
        totalRemainingUSD,
      };
    });
  }, [customers, products]);

  // Filter only customers with products and sort based on selected option
  const sortedCustomers = useMemo(() => {
    const customersWithProducts = customersWithTotals.filter(c => c.totalProducts > 0);
    return [...customersWithProducts].sort((a, b) => {
      switch (sortOption) {
        case 'code-asc':
          return Number(a.customerCode) - Number(b.customerCode);
        case 'code-desc':
          return Number(b.customerCode) - Number(a.customerCode);
        case 'name-asc':
          return a.fullName.localeCompare(b.fullName, 'ar');
        case 'name-desc':
          return b.fullName.localeCompare(a.fullName, 'ar');
        case 'remaining-desc':
          return (b.totalRemainingIQD - a.totalRemainingIQD) || (b.totalRemainingUSD - a.totalRemainingUSD);
        case 'remaining-asc':
          return (a.totalRemainingIQD - b.totalRemainingIQD) || (a.totalRemainingUSD - b.totalRemainingUSD);
        default:
          return 0; // بێ ڕیزکردن
      }
    });
  }, [customersWithTotals, sortOption]);

  // Pagination
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (sortedCustomers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ پارەدانێک نییە
      </div>
    );
  }

  return (
    <>
      {/* Sort Filter Button */}
      <div className="mb-4 flex items-center gap-2 px-2 md:px-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">
                {sortOptions.find(opt => opt.value === sortOption)?.label || 'ڕیزکردن'}
              </span>
              <span className="sm:hidden">ڕیزکردن</span>
              <ChevronDown className="h-3 w-3 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {sortOptions.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortOption(option.value)}
                  className={sortOption === option.value ? 'bg-primary/10 text-primary' : ''}
                >
                  <Icon className="h-4 w-4 ml-2" />
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Desktop Table */}
      <div className="rounded-md border hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">کۆد</TableHead>
              <TableHead className="text-right">ناو</TableHead>
              <TableHead className="text-right">کۆی کاڵاکان</TableHead>
              <TableHead className="text-right">کۆی فرۆشتنەکان</TableHead>
              <TableHead className="text-right">بڕی وەرگیراو</TableHead>
              <TableHead className="text-right">بڕی ماوە</TableHead>
              <TableHead className="text-right w-24">کردار</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCustomers.map((customer, index) => (
              <TableRow 
                key={customer.id} 
                className={`h-16 ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
              >
                <TableCell className="py-2">
                  <Badge variant="outline">{customer.customerCode}</Badge>
                </TableCell>
                <TableCell className="font-medium py-2">{customer.fullName}</TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalProductsIQD > 0 && (
                      <div className="text-right">
                        {customer.totalProductsIQD}
                      </div>
                    )}
                    {customer.totalProductsUSD > 0 && (
                      <div className="text-right">
                        {customer.totalProductsUSD}
                      </div>
                    )}
                    {customer.totalProductsIQD === 0 && customer.totalProductsUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalSaleIQD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start">
                        <span className="font-medium" dir="ltr">{customer.totalSaleIQD.toLocaleString()}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalSaleUSD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start">
                        <span className="font-medium" dir="ltr">{customer.totalSaleUSD.toLocaleString()}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">$</Badge>
                      </div>
                    )}
                    {customer.totalSaleIQD === 0 && customer.totalSaleUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalPaidIQD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start text-green-600">
                        <span className="font-medium" dir="ltr">{customer.totalPaidIQD.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalPaidUSD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start text-green-600">
                        <span className="font-medium" dir="ltr">{customer.totalPaidUSD.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">$</Badge>
                      </div>
                    )}
                    {customer.totalPaidIQD === 0 && customer.totalPaidUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalRemainingIQD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start">
                        <span className="font-semibold text-destructive" dir="ltr">{customer.totalRemainingIQD.toLocaleString()}</span>
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalRemainingUSD > 0 && (
                      <div className="flex items-center gap-1.5 justify-start">
                        <span className="font-semibold text-destructive" dir="ltr">{customer.totalRemainingUSD.toLocaleString()}</span>
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">$</Badge>
                      </div>
                    )}
                    {customer.totalRemainingIQD === 0 && customer.totalRemainingUSD === 0 && (
                      <Badge variant="outline" className="text-green-600 border-green-600">تەواو</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-8 cursor-pointer"
                    onClick={() => handleViewCustomer(customer)}
                  >
                    پارەدان
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={sortedCustomers.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {paginatedCustomers.map((customer, index) => {
          const isExpanded = expandedCustomers.has(customer.id);
          return (
            <div key={customer.id} className={`rounded-xl border bg-card overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${index % 2 === 1 ? 'bg-muted/60' : ''}`}>
              <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(customer.id)}>
                {/* Main Row */}
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">{customer.customerCode}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{customer.fullName}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 h-4">
                        {customer.totalProducts} کاڵا
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {customer.phone1}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(customer.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {/* Mini Table - IQD */}
                {customer.totalProductsIQD > 0 && (
                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">فرۆشتن</div>
                      <div className="font-semibold flex items-center justify-center gap-1">
                        <span dir="ltr">{customer.totalSaleIQD.toLocaleString()}</span>
                        <span className="text-[10px] text-muted-foreground">د.ع</span>
                      </div>
                    </div>
                    <div className="text-center border-x border-dashed">
                      <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                      <div className="font-semibold text-green-600 flex items-center justify-center gap-1">
                        <span dir="ltr">{customer.totalPaidIQD.toLocaleString()}</span>
                        <span className="text-[10px]">د.ع</span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">ماوە</div>
                      <div className="font-semibold text-red-500 flex items-center justify-center gap-1">
                        <span dir="ltr">{customer.totalRemainingIQD.toLocaleString()}</span>
                        <span className="text-[10px]">د.ع</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Mini Table - USD */}
                {customer.totalProductsUSD > 0 && (
                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-emerald-500/10 rounded-lg p-2 w-full" dir="rtl">
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">فرۆشتن</div>
                      <div className="font-semibold flex items-center justify-center gap-0.5" dir="ltr">
                        <span className="text-[10px] text-muted-foreground">$</span>
                        <span>{customer.totalSaleUSD.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="text-center border-x border-dashed">
                      <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                      <div className="font-semibold text-green-600 flex items-center justify-center gap-0.5" dir="ltr">
                        <span className="text-[10px]">$</span>
                        <span>{customer.totalPaidUSD.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">ماوە</div>
                      <div className="font-semibold text-red-500 flex items-center justify-center gap-0.5" dir="ltr">
                        <span className="text-[10px]">$</span>
                        <span>{customer.totalRemainingUSD.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                  <div className="space-y-2 pt-3">
                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                      <span className="text-muted-foreground text-xs">ژمارەی کاڵاکان</span>
                      <span className="font-medium text-sm">{customer.totalProducts}</span>
                    </div>
                    {customer.job && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">پیشە</span>
                        <span className="font-medium text-sm">{customer.job}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ناونیشان</span>
                        <span className="font-medium text-sm">{customer.address}</span>
                      </div>
                    )}
                    <Button 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCustomer(customer);
                      }}
                    >
                      پارەدان
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={sortedCustomers.length}
          itemsPerPage={itemsPerPage}
        />
      </div>
    </>
  );
}
